import React, { useEffect } from 'react';
import { ArrowRight, Beaker, ShieldCheck, Zap } from 'lucide-react';
import SEO from './SEO';

interface Props {
  onNavigate: (view: any, sectionId?: string) => void;
}

const PrivateLabelSupplements: React.FC<Props> = ({ onNavigate }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-24 pb-16 bg-white">
      <SEO 
        title="Private Label Supplements | Custom Formulations BuildMySupplement" 
        description="Build a truly unique brand with custom formulations. As your private label supplement manufacturer, we offer low MOQ custom manufacturing to create your competitive edge." 
      />
      
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-gray-100 border border-gray-200 mb-6">
            <span className="text-gray-800 text-xs font-bold uppercase tracking-widest">
              Customization & Differentiation
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 tracking-tight mb-8 leading-tight">
            Your Formula. Your Brand. Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500">Competitive Edge.</span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-3xl mx-auto">
            In a crowded wellness market, differentiation is everything. Don't just slap a label on a generic blend—engineer a proprietary formula that your competitors simply cannot replicate. Build a dynasty of loyal customers with our premium private label manufacturing.
          </p>
          <button 
            onClick={() => onNavigate('contact')}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-black rounded-full hover:bg-gray-800 transition-all duration-200 transform hover:-translate-y-1"
          >
            Start Custom Formulation <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Content Sections */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-lg text-gray-700 space-y-12">
        
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">The Power of Proprietary: Why Choose Private Label Supplements?</h2>
          <p className="mb-4 leading-relaxed">
            While white labeling is incredible for speed to market, true brand equity is forged in the fires of exclusivity. When you utilize <strong>private label supplements</strong>, you retain complete ownership over the specific formulation. If a customer falls in love with the exact flavor profile of your pre-workout, or the precise blend of your adaptogen powder, they have to come back to <em>you</em>. They cannot find it on a competitor's site.
          </p>
          <p className="mb-4 leading-relaxed">
            Private labeling grants you the freedom to identify gaps in the market and fill them perfectly. Do you want to add a clinical dose of a patented ingredient to a standard multivitamin? Let's do it. Do you want to formulate a vegan-friendly, stevia-sweetened hydration matrix? The lab is yours. We act as an invisible extension of your brand, executing your vision with scientific precision.
          </p>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">Partnering with a Premium Private Label Supplement Manufacturer</h2>
          <p className="mb-4 leading-relaxed">
            Choosing your <strong>private label supplement manufacturer</strong> is the most consequential decision you will make for your brand. A poor manufacturing partner will plague you with wildly inconsistent flavor profiles, lumpy powders, missed deadlines, and regulatory nightmares. A great partner, however, fuels your growth.
          </p>
          <p className="mb-4 leading-relaxed">
            We operate out of state-of-the-art, GMP-certified facilities equipped with next-generation blending and encapsulation technology. Our R&D team comprises industry-leading food scientists and flavor chemists who understand how to mask bitter botanicals and stabilize complex compounds. When you partner with us, you are not just getting a factory; you are inheriting decades of nutraceutical engineering expertise aimed directly at making your product the best in its class.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 mt-10">
            <div className="bg-gray-50 p-6 rounded-2xl">
              <Beaker className="w-8 h-8 text-orange-600 mb-4" />
              <h4 className="font-bold text-gray-900 mb-2">Expert R&D</h4>
              <p className="text-sm">World-class chemists reverse-engineer competitors or build your unique vision from the ground up.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-2xl">
              <ShieldCheck className="w-8 h-8 text-orange-600 mb-4" />
              <h4 className="font-bold text-gray-900 mb-2">Rigorous Testing</h4>
              <p className="text-sm">Microbiological and heavy metal testing ensures your proprietary formula is as safe as it is potent.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-2xl">
              <Zap className="w-8 h-8 text-orange-600 mb-4" />
              <h4 className="font-bold text-gray-900 mb-2">Scalable Output</h4>
              <p className="text-sm">From your first pilot run to mass-market retail volumes, our infrastructure scales with you.</p>
            </div>
          </div>
        </div>

        <div>
           <h2 className="text-3xl font-extrabold text-gray-900 mb-6">Democratizing Production: Private Label Supplements Low MOQ</h2>
          <p className="mb-4 leading-relaxed">
            Historically, custom formulation was reserved solely for legacy brands with massive budgets. Manufacturers would demand minimum order quantities of 10,000 to 50,000 units just to turn the blenders on. This dynamic locked passionate innovators out of the market.
          </p>
          <p className="mb-4 leading-relaxed">
            We have engineered our production lines to support <strong>private label supplements low MOQ</strong>. We believe that if you have a killer concept for a <strong>custom supplement formulation</strong>, you shouldn't need a venture capital firm backing you to bring it to life. Our low minimums allow you to test your unique formulation with your core audience, gather invaluable feedback, and scale inventory only when the demand requires it, perfectly mitigating your financial risk.
          </p>
        </div>

        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">The Journey to Your Custom Supplement Formulation</h2>
          <p className="mb-4 leading-relaxed">
            Creating a custom product should feel like a creative collaboration, not a bureaucratic nightmare. We guide you through an optimized, transparent process:
          </p>
          <ul className="space-y-6 mt-6">
            <li className="flex">
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-black text-white font-bold text-lg">1</div>
              <div className="ml-5">
                <h4 className="text-xl font-bold text-gray-900">Concept & Profiling</h4>
                <p className="text-gray-600 mt-1">We discuss your target audience, desired clinical outcomes, and strict ingredient preferences (vegan, keto, non-GMO).</p>
              </div>
            </li>
            <li className="flex">
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-black text-white font-bold text-lg">2</div>
              <div className="ml-5">
                <h4 className="text-xl font-bold text-gray-900">Formulation & Flavoring</h4>
                <p className="text-gray-600 mt-1">Our chemists create the active matrix. If it's a powder, we iterate through flavor samples until it tastes like a premium dessert.</p>
              </div>
            </li>
            <li className="flex">
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-black text-white font-bold text-lg">3</div>
              <div className="ml-5">
                <h4 className="text-xl font-bold text-gray-900">Testing & Compliance</h4>
                <p className="text-gray-600 mt-1">Rigorous stability testing and label reviews ensure you meet all FDA/FSSAI regulatory guidelines.</p>
              </div>
            </li>
            <li className="flex">
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-black text-white font-bold text-lg">4</div>
              <div className="ml-5">
                <h4 className="text-xl font-bold text-gray-900">Production & Scaling</h4>
                <p className="text-gray-600 mt-1">We run your low MOQ pilot batch, ship it out, and stand ready to handle 10x volume when your marketing campaign hits.</p>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-900 text-white py-20 mt-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-600/20 rounded-full blur-[100px] -mr-32 -mt-32 pointer-events-none"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-extrabold mb-6">Build Your Unfair Advantage.</h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Ready to formulate the product that will define your brand legacy? Speak with our R&D experts and get a quote on your custom private label project.
          </p>
          <button 
            onClick={() => onNavigate('contact')}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-gray-900 bg-white rounded-full hover:bg-gray-100 transition-all duration-200 transform hover:-translate-y-1"
          >
            Formulate With Us <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

    </div>
  );
};

export default PrivateLabelSupplements;
